/**
 * PlanHistoryModal - Modal d'historique des changements de plans
 * 
 * Timeline visuelle élégante des changements de plans avec détails complets.
 * Affiche transitions, montants, dates, statuts et raisons de changement.
 */

import React, { useRef, useEffect, useCallback, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import PlansService from '../../services/api/plansService';
import { formatDateTime } from '../../utils/formatDate';

interface PlanHistoryModalProps {
    id: string;
    companyId: string;
    accessToken: string;
    onClose?: () => void;
}

const PlanHistoryModal: React.FC<PlanHistoryModalProps> = ({
    id,
    companyId,
    accessToken,
    onClose
}) => {
    const { t } = useTranslation();
    const closeRef = useRef<HTMLButtonElement>(null);
    const modalRef = useRef<HTMLDivElement>(null);
    
    const [loading, setLoading] = useState(true);
    const [history, setHistory] = useState<any[]>([]);
    const [total, setTotal] = useState(0);
    const [limit] = useState(50);
    const [skip, setSkip] = useState(0);

    // Charger l'historique depuis le backend
    useEffect(() => {
        const loadHistory = async () => {
            setLoading(true);
            try {
                const response = await PlansService.getPlanHistory(companyId, limit, skip, accessToken);
                if (response.success && response.data) {
                    setHistory(response.data.history || []);
                    setTotal(response.data.total || 0);
                } else {
                    console.error('[PlanHistoryModal] Failed to load plan history:', response);
                    setHistory([]);
                    setTotal(0);
                }
            } catch (error) {
                console.error('[PlanHistoryModal] Error loading plan history:', error);
                setHistory([]);
                setTotal(0);
            } finally {
                setLoading(false);
            }
        };

        loadHistory();
    }, [companyId, accessToken, limit, skip]);

    const handleClose = useCallback(() => {
        if (onClose) {
            onClose();
        }
        document.body.style.overflow = '';
    }, [onClose]);

    useEffect(() => {
        const modalElement = modalRef.current;
        if (modalElement) {
            const handleModalHidden = () => {
                handleClose();
            };
            modalElement.addEventListener('hidden.bs.modal', handleModalHidden);
            document.body.style.overflow = 'hidden';

            // Ouvrir le modal automatiquement
            const bootstrap = (window as any).bootstrap;
            if (bootstrap) {
                const modal = bootstrap.Modal.getOrCreateInstance(modalElement);
                modal.show();
            }

            return () => {
                modalElement.removeEventListener('hidden.bs.modal', handleModalHidden);
                document.body.style.overflow = '';
            };
        }
    }, [handleClose]);

    const getPlanBadgeClass = (plan: string): string => {
        switch (plan) {
            case 'FREE': return 'bg-secondary';
            case 'ESSENTIAL': return 'bg-warning text-dark';
            case 'PLUS': return 'bg-primary';
            case 'ENTERPRISE': return 'bg-success';
            default: return 'bg-secondary';
        }
    };

    const getStatusBadgeClass = (status: string): string => {
        switch (status) {
            case 'ACTIVE': return 'bg-success';
            case 'EXPIRED': return 'bg-secondary';
            case 'CANCELLED': return 'bg-danger';
            case 'UPGRADED': return 'bg-info';
            case 'DOWNGRADED': return 'bg-warning text-dark';
            default: return 'bg-secondary';
        }
    };

    const getStatusIcon = (status: string): string => {
        switch (status) {
            case 'ACTIVE': return 'fa-check-circle';
            case 'EXPIRED': return 'fa-clock';
            case 'CANCELLED': return 'fa-times-circle';
            case 'UPGRADED': return 'fa-arrow-up';
            case 'DOWNGRADED': return 'fa-arrow-down';
            default: return 'fa-circle';
        }
    };

    // Utilisation de notre fonction utilitaire formatDateTime

    const modalContent = (
        <div
            ref={modalRef}
            className="modal fade"
            data-bs-backdrop="true"
            data-bs-keyboard="true"
            id={id}
            tabIndex={-1}
            aria-labelledby={`${id}Label`}
            aria-hidden="true"
            style={{ zIndex: 10000, position: 'fixed' }}
        >
            <div className="modal-dialog modal-dialog-centered modal-xl modal-dialog-scrollable" style={{ zIndex: 10001, pointerEvents: 'auto' }}>
                <div className="modal-content" style={{ zIndex: 10002, pointerEvents: 'auto', backgroundColor: '#1a1a1a', borderRadius: '15px' }}>
                    <div className="modal-header border-secondary" style={{ borderRadius: '15px 15px 0 0' }}>
                        <h5 className="modal-title text-theme" id={`${id}Label`}>
                            <i className="fa-solid fa-history me-2"></i>
                            {t('views.modal.planHistory.title', 'Historique des plans')}
                            {total > 0 && <span className="badge bg-primary ms-2">{total}</span>}
                        </h5>
                        <button
                            type="button"
                            className="btn-close btn-close-white"
                            data-bs-dismiss="modal"
                            aria-label="Close"
                            onClick={handleClose}
                            style={{ pointerEvents: 'auto', cursor: 'pointer' }}
                        ></button>
                    </div>
                    <div className="modal-body text-white p-4" style={{ maxHeight: '70vh', overflowY: 'auto' }}>
                        {loading ? (
                            <div className="text-center py-5">
                                <div className="spinner-border text-primary" role="status">
                                    <span className="visually-hidden">Loading...</span>
                                </div>
                                <p className="mt-3 text-muted">{t('views.modal.planHistory.loading', 'Chargement de l\'historique...')}</p>
                            </div>
                        ) : history.length > 0 ? (
                            <div className="position-relative">
                                {/* Timeline verticale */}
                                <div className="position-absolute top-0 start-0 h-100" style={{ left: '30px', width: '2px', background: 'linear-gradient(to bottom, #007bff, #6610f2)', opacity: 0.3 }}></div>
                                
                                {history.map((entry: any, index: number) => (
                                    <div key={entry.id || index} className="position-relative mb-4 ps-5">
                                        {/* Icône timeline */}
                                        <div 
                                            className="position-absolute top-0 start-0 d-flex align-items-center justify-content-center"
                                            style={{
                                                width: '60px',
                                                height: '60px',
                                                left: '0px',
                                                marginTop: '10px'
                                            }}
                                        >
                                            <div 
                                                className={`rounded-circle d-flex align-items-center justify-content-center`}
                                                style={{
                                                    width: '50px',
                                                    height: '50px',
                                                    background: 'linear-gradient(135deg, #007bff, #0056b3)',
                                                    boxShadow: '0 4px 15px rgba(0, 123, 255, 0.4)',
                                                    border: '3px solid #1a1a1a'
                                                }}
                                            >
                                                <i className={`fa-solid ${getStatusIcon(entry.status)} text-white`}></i>
                                            </div>
                                        </div>

                                        {/* Carte détails */}
                                        <div 
                                            className="card border-secondary"
                                            style={{
                                                backgroundColor: '#2a2a2a',
                                                borderRadius: '12px',
                                                borderLeft: entry.status === 'ACTIVE' ? '4px solid #28a745' : '1px solid #495057',
                                                marginLeft: '20px'
                                            }}
                                        >
                                            <div className="card-body p-3">
                                                {/* Header */}
                                                <div className="d-flex justify-content-between align-items-start mb-3">
                                                    <div className="d-flex align-items-center gap-2 flex-wrap">
                                                        {entry.previous_plan && (
                                                            <>
                                                                <span className={`badge ${getPlanBadgeClass(entry.previous_plan)} px-3 py-2`}>
                                                                    {entry.previous_plan}
                                                                </span>
                                                                <i className="fa-solid fa-arrow-right text-muted"></i>
                                                            </>
                                                        )}
                                                        <span className={`badge ${getPlanBadgeClass(entry.plan)} px-3 py-2`}>
                                                            {entry.plan}
                                                        </span>
                                                        <span className={`badge ${getStatusBadgeClass(entry.status)} px-2 py-1`}>
                                                            {entry.status}
                                                        </span>
                                                    </div>
                                                    {entry.amount_paid > 0 && (
                                                        <span className="badge bg-info px-3 py-2">
                                                            ${entry.amount_paid}
                                                        </span>
                                                    )}
                                                </div>

                                                {/* Détails */}
                                                <div className="row g-2 small">
                                                    <div className="col-md-6">
                                                        <div className="d-flex align-items-center mb-2">
                                                            <i className="fa-solid fa-calendar-start text-success me-2" style={{ width: '20px' }}></i>
                                                            <span className="text-muted me-2">Début:</span>
                                                            <strong className="text-white">{formatDateTime(entry.start_date)}</strong>
                                                        </div>
                                                    </div>
                                                    {entry.end_date && (
                                                        <div className="col-md-6">
                                                            <div className="d-flex align-items-center mb-2">
                                                                <i className="fa-solid fa-calendar-xmark text-danger me-2" style={{ width: '20px' }}></i>
                                                                <span className="text-muted me-2">Fin:</span>
                                                                <strong className="text-white">{formatDateTime(entry.end_date)}</strong>
                                                            </div>
                                                        </div>
                                                    )}
                                                    <div className="col-md-6">
                                                        <div className="d-flex align-items-center mb-2">
                                                            <i className="fa-solid fa-coins text-warning me-2" style={{ width: '20px' }}></i>
                                                            <span className="text-muted me-2">Crédits alloués:</span>
                                                            <strong className="text-white">{entry.credits_allocated}</strong>
                                                        </div>
                                                    </div>
                                                    {entry.change_reason && (
                                                        <div className="col-md-6">
                                                            <div className="d-flex align-items-center mb-2">
                                                                <i className="fa-solid fa-tag text-info me-2" style={{ width: '20px' }}></i>
                                                                <span className="text-muted me-2">Raison:</span>
                                                                <strong className="text-white">{entry.change_reason}</strong>
                                                            </div>
                                                        </div>
                                                    )}
                                                    {entry.stripe_subscription_id && (
                                                        <div className="col-12">
                                                            <div className="d-flex align-items-center">
                                                                <i className="fa-brands fa-stripe text-primary me-2" style={{ width: '20px' }}></i>
                                                                <span className="text-muted me-2">Subscription ID:</span>
                                                                <code className="text-white small">{entry.stripe_subscription_id}</code>
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="text-center py-5">
                                <i className="fa-solid fa-inbox text-muted mb-3" style={{ fontSize: '4rem' }}></i>
                                <p className="text-muted">{t('views.modal.planHistory.empty', 'Aucun historique de plan disponible')}</p>
                            </div>
                        )}
                    </div>
                    <div className="modal-footer border-secondary">
                        <button
                            ref={closeRef}
                            type="button"
                            className="btn btn-secondary"
                            data-bs-dismiss="modal"
                            onClick={handleClose}
                            style={{ pointerEvents: 'auto', cursor: 'pointer' }}
                        >
                            <i className="fa-solid fa-times me-2"></i>
                            {t('words.close', 'Fermer')}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );

    return createPortal(modalContent, document.body);
};

export default PlanHistoryModal;
